
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import HtmlEditor from '../../Common/HtmlEditor';
import { toast } from 'react-toastify';
import apiClient from '../../utils/apiClient';

const AddEditFaqs = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const editingFaq = state?.faq;

    const [formData, setFormData] = useState({
        question: '',
        answer: '',
        sequenceOrder: '',
        faqType: '',
    });

    const [validateErrors, setValidateErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState('')
    const faqTypes = ['user', 'technician'];

    useEffect(() => {
        if (editingFaq) {
            setFormData({
                question: editingFaq.question || '',
                answer: editingFaq.answer || '',
                sequenceOrder: editingFaq.sequenceOrder?.toString() || '',
                faqType: editingFaq.type || '',
            });
        }
    }, [editingFaq]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (['sequenceOrder'].includes(name)) {
            // Allow only numbers or empty string
            if (value === '' || /^[0-9\b]+$/.test(value)) {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Clear individual field error
        setValidateErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleAnswerChange = (content) => {
        setFormData(prev => ({ ...prev, answer: content }));

        const stripped = content.replace(/<[^>]+>/g, '').trim();
        if (stripped) {
            setValidateErrors(prev => ({ ...prev, answer: '' }));
        }
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.sequenceOrder || parseInt(formData.sequenceOrder) < 1) {
            errors.sequenceOrder = 'Sequence Order must be at least 1';
        }

        if (!formData.question.trim()) {
            errors.question = 'Question is required';
        }

        const strippedAnswer = formData.answer.replace(/<[^>]+>/g, '').trim();
        if (!strippedAnswer) {
            errors.answer = 'Answer is required';
        }

        if (!formData.faqType || !faqTypes.includes(formData.faqType)) {
            errors.faqType = 'Please select a valid FAQ type';
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const errors = validateForm();
            if (Object.keys(errors).length > 0) {
                setValidateErrors(errors);
                return;
            }

            const payload = {
                question: formData.question,
                answer: formData.answer,
                sequenceOrder: parseInt(formData.sequenceOrder),
                type: formData.faqType,
            };
            let response;

            if (editingFaq) {
                response = await apiClient.put(`/faq/updatefaq/${editingFaq.id}`, payload);
            } else {
                response = await apiClient.post(`/faq/createfaq`, payload);
            }

            if (response.data.status === true) {
                navigate('/display-faqs');
                toast.success(response.data.message)
            }

        } catch (error) {
            setErrors(error?.response?.data?.message || 'Something went wrong. Please try again later.')
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={{ sm: 2, xs: 2, md: 4, lg: 4 }}>
            <Typography variant="h4" mb={3}>
                {editingFaq ? 'Edit FAQ' : 'Add FAQ'}
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500, border: '1px solid lightgrey', borderRadius: 1, p: 2 }}>

                {/* FAQ Type */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }} error={!!validateErrors.faqType}>
                    <InputLabel id="faq-type-label">FAQ Type</InputLabel>
                    <Select
                        labelId="faq-type-label"
                        name="faqType"
                        value={formData.faqType}
                        label="FAQ Type"
                        onChange={handleInputChange}
                    >
                        {faqTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                    {validateErrors.faqType && <FormHelperText>{validateErrors.faqType}</FormHelperText>}
                </FormControl>

                {/* Sequence Order */}
                <TextField
                    label="Sequence Order"
                    name="sequenceOrder"
                    type="text"
                    fullWidth
                    size="small"
                    value={formData.sequenceOrder}
                    onChange={handleInputChange}
                    sx={{ mb: 2 }}
                    error={!!validateErrors.sequenceOrder}
                    helperText={validateErrors.sequenceOrder}
                />

                {/* Question */}
                <TextField
                    label="Question"
                    name="question"
                    fullWidth
                    size="small"
                    value={formData.question}
                    onChange={handleInputChange}
                    sx={{ mb: 2 }}
                    error={!!validateErrors.question}
                    helperText={validateErrors.question}
                />

                {/* Answer */}
                <Typography variant="subtitle1" mb={1}>Answer</Typography>
                <HtmlEditor
                    value={formData.answer}
                    onChange={handleAnswerChange}
                />
                {validateErrors.answer && (
                    <Typography variant="caption" color="error" sx={{ mb: 2, display: 'block' }}>
                        {validateErrors.answer}
                    </Typography>
                )}

                {errors && <Typography variant='body2' color='error'>{errors}</Typography>}

                {/* Buttons */}
                <Box mt={3} display="flex" justifyContent="right" gap={2}>
                    <Button variant="outlined" color="primary" onClick={() => navigate('/display-faqs')}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {loading
                            ? (editingFaq ? 'Updating...' : 'Creating...')
                            : (editingFaq ? 'Update FAQ' : 'Create FAQ')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default AddEditFaqs;


